import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import { getData } from '../../Webservice/Webservice';
import { FONT } from './contentShared';
import { AUDIENCE_OPTIONS, Field, GHOST_BTN, ImageUpload, STATUS_OPTIONS, YELLOW_BTN } from './formShared';

const emptyForm = { type: 'Subcategory', linkId: '', status: 'active', showTo: 'all', position: '', image: null };

const SubcategoryForm = ({ open, handleClose, handleSubmit, isEdit, editingSubcategory }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [subcategories, setSubcategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // subcategories the item can point to
  useEffect(() => {
    const raw = sessionStorage.getItem('token');
    getData('subcategories/list?limit=1000', null, raw ? `Bearer ${raw}` : '')
      .then((res) => setSubcategories(res && Array.isArray(res.subcategories) ? res.subcategories : []))
      .catch(() => setSubcategories([]));
  }, []);

  useEffect(() => {
    setErrors({});
    if (isEdit && editingSubcategory) {
      setImagePreview(editingSubcategory.image || null);
      setFormData({
        type: editingSubcategory.type || 'Subcategory',
        linkId: editingSubcategory.linkId?._id || editingSubcategory.linkId || '',
        status: editingSubcategory.status || 'active',
        showTo: editingSubcategory.showTo || 'all',
        position: editingSubcategory.position || '',
        image: editingSubcategory.image || null,
      });
    } else {
      setImagePreview(null);
      setFormData(emptyForm);
    }
  }, [isEdit, editingSubcategory, open]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFile = (file) => {
    setFormData((prev) => ({ ...prev, image: file || prev.image }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, image: undefined }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!formData.linkId) next.linkId = 'Choose the subcategory this opens';
    if (!isEdit && !formData.image) next.image = 'An image is required';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await handleSubmit(formData);
    } finally {
      setSaving(false);
    }
  };

  const selected = subcategories.find((s) => s._id === formData.linkId) || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20, color: '#0f172a' }}>{isEdit ? 'Edit Home Category' : 'Add Home Category'}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>An image shortcut on the home screen that opens a subcategory.</Typography>
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <ImageUpload src={imagePreview} onFile={handleFile} label="Upload category image" error={errors.image} aspect="16 / 8" />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={subcategories}
                value={selected}
                getOptionLabel={(o) => o.title || ''}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                onChange={(_, v) => { setFormData((prev) => ({ ...prev, linkId: v?._id || '' })); if (errors.linkId) setErrors((p) => ({ ...p, linkId: undefined })); }}
                renderInput={(params) => <TextField {...params} size="small" label="Subcategory" required error={!!errors.linkId} helperText={errors.linkId} />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field label="Position" name="position" type="number" inputProps={{ min: 1 }} value={formData.position} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label="Status" name="status" value={formData.status} onChange={handleFormChange} options={STATUS_OPTIONS} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Field label="Show to" name="showTo" value={formData.showTo} onChange={handleFormChange} options={AUDIENCE_OPTIONS} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
          <Button variant="outlined" onClick={handleClose} sx={GHOST_BTN}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={YELLOW_BTN}>{saving ? 'Saving…' : isEdit ? 'Update' : 'Add category'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SubcategoryForm;
